import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { collection, getDocs, doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore'; // Added deleteDoc
import CryptoJS from 'crypto-js'; // Added for secure Cloudinary cryptographic signing

function ReportsManagement() {
  const [activeSubTab, setActiveSubTab] = useState('room_report'); // 'room_report' or 'user_report'
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoadingId, setActionLoadingId] = useState(null);

  // Cloudinary storage parameters matching your project configurations
  const CLOUD_NAME = 'dnlaudxq2';
  const API_KEY = '396776948922162';
  const API_SECRET = 'rxKOlhA8rOtUKDOZPfFGEQju29A';

  // Synchronize stream across database boundaries
  const fetchAllReports = async () => {
    setLoading(true);
    setError('');
    try {
      const querySnapshot = await getDocs(collection(db, 'reports'));
      const fetchedReports = [];

      for (const reportDoc of querySnapshot.docs) {
        const reportData = reportDoc.data();
        
        // PIPELINE 1: Process Room Flag Records
        if (reportData.reportType === 'room_report') {
          let roomDetails = { targetTitle: 'Unknown Listing', targetContext: 'N/A', currentStatus: 'N/A', imageUrls: [] };
          
          if (reportData.reportedOwnerId && reportData.reportedRoomId) {
            try {
              const roomSnap = await getDoc(
                doc(db, 'roomsData', reportData.reportedOwnerId, 'rooms', reportData.reportedRoomId)
              );
              if (roomSnap.exists()) {
                const rData = roomSnap.data();
                roomDetails = {
                  targetTitle: rData.title || 'Untitled Room',
                  targetContext: rData.place || 'Unknown Location',
                  currentStatus: rData.status || 'active',
                  imageUrls: rData.imageUrls || [] // Retain image array references for deep wiping
                };
              } else {
                roomDetails = { targetTitle: 'Removed Listing', targetContext: 'N/A', currentStatus: 'deleted', imageUrls: [] };
              }
            } catch (err) {
              console.warn("Failed verifying subcollection document bindings:", err);
            }
          }
          
          fetchedReports.push({
            id: reportDoc.id,
            ...reportData,
            ...roomDetails
          });
        } 
        
        // PIPELINE 2: Process User Account Flag Records
        else if (reportData.reportType === 'user_report') {
          let userDetails = { targetTitle: 'Unknown Account', targetContext: 'N/A', currentStatus: 'active' };
          
          if (reportData.reportedUserId) {
            try {
              const userSnap = await getDoc(doc(db, 'users', reportData.reportedUserId));
              if (userSnap.exists()) {
                const uData = userSnap.data();
                userDetails = {
                  targetTitle: `${uData.firstName || 'User'} ${uData.lastName || ''}`.trim(),
                  targetContext: uData.email || 'No email associated',
                  currentStatus: uData.accountStatus || 'active'
                };
              } else {
                userDetails = { targetTitle: 'Deleted User Profile', targetContext: 'N/A', currentStatus: 'deleted' };
              }
            } catch (err) {
              console.warn("Failed verifying root user collection reference paths:", err);
            }
          }

          fetchedReports.push({
            id: reportDoc.id,
            ...reportData,
            ...userDetails
          });
        }
      }

      setReports(fetchedReports);
    } catch (err) {
      console.error("Database tracking stream down:", err);
      setError('Failed to aggregate system violation records.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllReports();
  }, []);

  // Helper utility to safely execute image deletion requests straight from browser client
  const deleteFromCloudinary = async (publicId) => {
    const timestamp = Math.round(new Date().getTime() / 1000);
    const stringToSign = `public_id=${publicId}&timestamp=${timestamp}${API_SECRET}`;
    const signature = CryptoJS.SHA1(stringToSign).toString();

    const formData = new FormData();
    formData.append('public_id', publicId);
    formData.append('signature', signature);
    formData.append('api_key', API_KEY);
    formData.append('timestamp', timestamp);

    try {
      await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/destroy`, {
        method: 'POST',
        body: formData,
      });
      console.log(`Cloudinary asset wiped successfully: ${publicId}`);
    } catch (err) {
      console.error("Cloudinary asset deletion connection fault:", err);
    }
  };

  // Action 1: Dismiss the issue (Updates report status to solved, keeps room active)
  const handleDismissReport = async (reportId) => {
    if (!window.confirm('Mark this warning flag as SOLVED without changing content status?')) return;
    setActionLoadingId(reportId);
    try {
      await updateDoc(doc(db, 'reports', reportId), { status: 'solved' });
      setReports(prev => prev.map(r => r.id === reportId ? { ...r, status: 'solved' } : r));
      alert('Report state updated to SOLVED.');
    } catch (err) {
      alert('Failed to update log entry status.');
    } finally {
      setActionLoadingId(null);
    }
  };

  // Action 2: Take down room listing and resolve flag entry
  const handleTakeDownRoom = async (reportId, reportedOwnerId, reportedRoomId) => {
    if (!window.confirm('Take this listing offline and resolve the violation report?')) return;
    setActionLoadingId(reportId);
    try {
      await updateDoc(doc(db, 'roomsData', reportedOwnerId, 'rooms', reportedRoomId), { status: 'inactive' });
      await updateDoc(doc(db, 'reports', reportId), { status: 'solved' });
      
      setReports(prev => prev.map(r => r.id === reportId ? { ...r, status: 'solved', currentStatus: 'inactive' } : r));
      alert('Listing deactivated and report set to SOLVED.');
    } catch (err) {
      alert('Failed executing database modifications.');
    } finally {
      setActionLoadingId(null);
    }
  };

  // NEW ACTION: Permanent Room Document Wipe + Cloudinary Image Purge Sequence
  const handleDeleteRoom = async (reportId, reportedOwnerId, reportedRoomId, imageUrls) => {
    if (!window.confirm('⚠️ CRITICAL PERMANENT DELETION WARNING:\n\nThis will completely erase this room listing out of Firestore and wipe all uploaded display images from Cloudinary storage.\n\nThis operation cannot be undone. Proceed?')) return;
    
    setActionLoadingId(reportId);
    try {
      // 1. Cycle loop through attached property pictures and fire signed wipe requests
      if (imageUrls && imageUrls.length > 0) {
        console.log(`Clearing out ${imageUrls.length} media files from Cloudinary storage bins...`);
        for (const image of imageUrls) {
          const publicId = typeof image === 'object' ? image.public_id : null;
          if (publicId) {
            await deleteFromCloudinary(publicId);
          }
        }
      }

      // 2. Erase core property document path node from subcollection
      await deleteDoc(doc(db, 'roomsData', reportedOwnerId, 'rooms', reportedRoomId));

      // 3. Flag violation logging ticket status parameters to solved
      await updateDoc(doc(db, 'reports', reportId), { status: 'solved' });

      setReports(prev => prev.map(r => r.id === reportId ? { ...r, status: 'solved', currentStatus: 'deleted' } : r));
      alert('Listing permanently removed from database ecosystem and ticket closed.');
    } catch (err) {
      console.error("Permanent room removal fault:", err);
      alert(`Wipe sequence failed: ${err.message}`);
    } finally {
      setActionLoadingId(null);
    }
  };

  // Action 3: Suspend profile authorization boundaries and resolve flag entry
  const handleSuspendUser = async (reportId, reportedUserId) => {
    if (!window.confirm('Suspend user authentication node and resolve this profile report?')) return;
    setActionLoadingId(reportId);
    try {
      await updateDoc(doc(db, 'users', reportedUserId), { accountStatus: 'suspended' });
      await updateDoc(doc(db, 'reports', reportId), { status: 'solved' });
      
      setReports(prev => prev.map(r => r.id === reportId ? { ...r, status: 'solved', currentStatus: 'suspended' } : r));
      alert('User suspended and report set to SOLVED.');
    } catch (err) {
      alert('Failed processing profile restriction block update.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const filteredReports = reports.filter(r => r.reportType === activeSubTab);

  if (loading) return <div style={styles.loading}>Connecting data sync streams...</div>;
  if (error) return <div style={styles.error}>{error}</div>;

  return (
    <div style={styles.container}>
      <style>{`
        @media (max-width: 900px) {
          .desktop-panel { display: none !important; }
          .mobile-panel { display: block !important; }
        }
        @media (min-width: 901px) {
          .mobile-panel { display: none !important; }
        }
      `}</style>

      {/* Filter Tabs Toggle area */}
      <div style={styles.tabBar}>
        <button 
          onClick={() => setActiveSubTab('room_report')} 
          style={activeSubTab === 'room_report' ? styles.activeTabBtn : styles.inactiveTabBtn}
        >
          Flagged Rooms ({reports.filter(r => r.reportType === 'room_report').length})
        </button>
        <button 
          onClick={() => setActiveSubTab('user_report')} 
          style={activeSubTab === 'user_report' ? styles.activeTabBtn : styles.inactiveTabBtn}
        >
          Flagged Users ({reports.filter(r => r.reportType === 'user_report').length})
        </button>
      </div>

      <h3 style={styles.title}>
        Violation Center Center: {activeSubTab === 'room_report' ? 'Room Fe feeds' : 'User Accounts'}
      </h3>

      {filteredReports.length === 0 ? (
        <p style={styles.noData}>No records found matching this filtering tab query parameters.</p>
      ) : (
        <>
          {/* DESKTOP EVALUATION SPREADSHEET TABLE */}
          <table style={styles.table} className="desktop-panel">
            <thead>
              <tr>
                <th style={styles.th}>{activeSubTab === 'room_report' ? 'Flagged Room Title' : 'Reported User Name'}</th>
                <th style={styles.th}>{activeSubTab === 'room_report' ? 'Location Place' : 'Registered Email Node'}</th>
                <th style={styles.th}>Violation Code Note</th>
                <th style={styles.th}>Live Target Status</th>
                <th style={styles.th}>Report Status</th>
                <th style={styles.th}>Moderator Control Box</th>
              </tr>
            </thead>
            <tbody>
              {filteredReports.map(report => (
                <tr key={report.id} style={styles.tr}>
                  <td style={styles.td}>
                    <strong>{report.targetTitle}</strong>
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                      ID Ref: {report.reportedRoomId || report.reportedUserId}
                    </div>
                  </td>
                  <td style={styles.td}>{report.targetContext}</td>
                  <td style={styles.td}><span style={styles.reasonText}>⚠️ {report.reason || 'Infraction flag'}</span></td>
                  <td style={styles.td}>
                    <span style={report.currentStatus === 'active' ? styles.activeBadge : styles.inactiveBadge}>
                      {report.currentStatus.toUpperCase()}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={report.status === 'solved' ? styles.solvedReportBadge : styles.pendingReportBadge}>
                      {report.status ? report.status.toUpperCase() : 'PENDING'}
                    </span>
                  </td>
                  <td style={styles.tdActions}>
                    {report.status === 'solved' ? (
                      <span style={styles.resolvedText}>✓ Complete</span>
                    ) : (
                      <>
                        <button onClick={() => handleDismissReport(report.id)} disabled={actionLoadingId === report.id} style={styles.dismissBtn}>Dismiss</button>
                        
                        {/* Room report layout modifications toolbox */}
                        {activeSubTab === 'room_report' && report.currentStatus === 'active' && (
                          <button onClick={() => handleTakeDownRoom(report.id, report.reportedOwnerId, report.reportedRoomId)} disabled={actionLoadingId === report.id} style={styles.takedownBtn}>Take Down</button>
                        )}
                        {activeSubTab === 'room_report' && report.currentStatus !== 'deleted' && (
                          <button onClick={() => handleDeleteRoom(report.id, report.reportedOwnerId, report.reportedRoomId, report.imageUrls)} disabled={actionLoadingId === report.id} style={styles.delBtn}>Delete Room</button>
                        )}

                        {/* User report layout modifications toolbox */}
                        {activeSubTab === 'user_report' && report.currentStatus !== 'suspended' && (
                          <button onClick={() => handleSuspendUser(report.id, report.reportedUserId)} disabled={actionLoadingId === report.id} style={styles.takedownBtn}>Suspend</button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* MOBILE RESPONSIVE ADAPTIVE CARD WRAPPERS */}
          <div className="mobile-panel">
            {filteredReports.map(report => (
              <div key={report.id} style={styles.card}>
                <div style={styles.cardHeader}>
                  <strong>{report.targetTitle}</strong>
                  <span style={report.status === 'solved' ? styles.solvedReportBadge : styles.pendingReportBadge}>
                    {report.status ? report.status.toUpperCase() : 'PENDING'}
                  </span>
                </div>
                <div style={styles.cardBody}>
                  <p style={{ margin: '4px 0' }}><strong>Context Node:</strong> {report.targetContext}</p>
                  <p style={{ margin: '4px 0' }}><strong>Target Status:</strong> {report.currentStatus.toUpperCase()}</p>
                  <p style={{ margin: '8px 0', padding: '8px', backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: '6px', fontSize: '13px' }}>
                    <strong>Reason Filed:</strong> {report.reason || 'Flagged action review needed.'}
                  </p>
                </div>
                <div style={styles.cardActions}>
                  {report.status === 'solved' ? (
                    <div style={styles.mobileResolvedBanner}>Resolution Finalized (Audit History Saved)</div>
                  ) : (
                    <>
                      <button onClick={() => handleDismissReport(report.id)} disabled={actionLoadingId === report.id} style={{ ...styles.dismissBtn, flex: 1 }}>Dismiss</button>
                      
                      {activeSubTab === 'room_report' && report.currentStatus === 'active' && (
                        <button onClick={() => handleTakeDownRoom(report.id, report.reportedOwnerId, report.reportedRoomId)} disabled={actionLoadingId === report.id} style={{ ...styles.takedownBtn, flex: 1 }}>Take Down</button>
                      )}
                      {activeSubTab === 'room_report' && report.currentStatus !== 'deleted' && (
                        <button onClick={() => handleDeleteRoom(report.id, report.reportedOwnerId, report.reportedRoomId, report.imageUrls)} disabled={actionLoadingId === report.id} style={{ ...styles.delBtn, flex: 1 }}>Delete Room</button>
                      )}

                      {activeSubTab === 'user_report' && report.currentStatus !== 'suspended' && (
                        <button onClick={() => handleSuspendUser(report.id, report.reportedUserId)} disabled={actionLoadingId === report.id} style={{ ...styles.takedownBtn, flex: 1 }}>Suspend</button>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  /* Layout & Navigation */
  container: { 
    padding: '20px', 
    backgroundColor: '#fff', 
    borderRadius: '12px', 
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' 
  },

  tabBar: { 
    display: 'flex', 
    gap: '10px', 
    marginBottom: '25px', 
    borderBottom: '2px solid #f1f5f9', 
    paddingBottom: '10px' 
  },

  activeTabBtn: { 
    padding: '10px 20px', 
    backgroundColor: '#3b82f6', 
    color: 'white', 
    border: 'none', 
    borderRadius: '8px', 
    cursor: 'pointer', 
    fontWeight: '600', 
    fontSize: '14px' 
  },

  inactiveTabBtn: { 
    padding: '10px 20px', 
    backgroundColor: '#f1f5f9', 
    color: '#475569', 
    border: 'none', 
    borderRadius: '8px', 
    cursor: 'pointer', 
    fontWeight: '500', 
    fontSize: '14px' 
  },

  /* Typography & Tables */
  title: { 
    fontSize: '17px', 
    marginBottom: '15px', 
    color: '#1e293b', 
    fontWeight: '600' 
  },

  table: { 
    width: '100%', 
    borderCollapse: 'collapse' 
  },

  th: { 
    textAlign: 'left', 
    padding: '12px', 
    color: '#64748b', 
    fontSize: '13px', 
    borderBottom: '2px solid #f1f5f9', 
    fontWeight: '600' 
  },

  td: { 
    padding: '14px 12px', 
    fontSize: '13px', 
    borderBottom: '1px solid #f1f5f9', 
    color: '#334155' 
  },

  tdActions: { 
    padding: '12px', 
    borderBottom: '1px solid #f1f5f9', 
    display: 'flex', 
    gap: '4px', 
    alignItems: 'center', 
    minWidth: '220px', 
    flexWrap: 'wrap' 
  },

  /* Status Texts & Banners */
  reasonText: { 
    color: '#dc2626', 
    fontWeight: '500' 
  },

  resolvedText: { 
    color: '#16a34a', 
    fontWeight: '600', 
    fontSize: '14px', 
    paddingLeft: '8px' 
  },

  mobileResolvedBanner: { 
    width: '100%', 
    textAlign: 'center', 
    backgroundColor: '#f0fdf4', 
    color: '#166534', 
    padding: '10px', 
    borderRadius: '8px', 
    fontWeight: '500', 
    fontSize: '13px' 
  },

  activeBadge: { 
    backgroundColor: '#dcfce7', 
    color: '#166534', 
    padding: '4px 10px', 
    borderRadius: '20px', 
    fontSize: '11px', 
    fontWeight: 'bold' 
  },

  inactiveBadge: { 
    backgroundColor: '#fee2e2', 
    color: '#991b1b', 
    padding: '4px 10px', 
    borderRadius: '20px', 
    fontSize: '11px', 
    fontWeight: 'bold' 
  },

  pendingReportBadge: { 
    backgroundColor: '#fef3c7', 
    color: '#d97706', 
    padding: '4px 10px', 
    borderRadius: '20px', 
    fontSize: '11px', 
    fontWeight: 'bold' 
  },

  solvedReportBadge: { 
    backgroundColor: '#e2e8f0', 
    color: '#475569', 
    padding: '4px 10px', 
    borderRadius: '20px', 
    fontSize: '11px', 
    fontWeight: 'bold' 
  },
  
  // Admin button definitions
  dismissBtn: { 
    padding: '6px 12px', 
    backgroundColor: '#64748b', 
    color: 'white', 
    border: 'none', 
    borderRadius: '6px', 
    cursor: 'pointer', 
    fontSize: '12px', 
    fontWeight: '500' 
  },

  takedownBtn: { 
    padding: '6px 12px', 
    backgroundColor: '#f59e0b', 
    color: 'white', 
    border: 'none', 
    borderRadius: '6px', 
    cursor: 'pointer', 
    fontSize: '12px', 
    fontWeight: '500' 
  },

  delBtn: { 
    padding: '6px 12px', 
    backgroundColor: '#ef4444', 
    color: 'white', 
    border: 'none', 
    borderRadius: '6px', 
    cursor: 'pointer', 
    fontSize: '12px', 
    fontWeight: '500' 
  },
  
  /* Mobile Layout Elements */
  card: { 
    border: '1px solid #e2e8f0', 
    borderRadius: '12px', 
    padding: '16px', 
    marginBottom: '16px', 
    backgroundColor: '#f8fafc' 
  },

  cardHeader: { 
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'center', 
    borderBottom: '1px solid #e2e8f0', 
    paddingBottom: '8px' 
  },

  cardBody: { 
    padding: '10px 0', 
    color: '#475569', 
    fontSize: '14px' 
  },

  cardActions: { 
    display: 'flex', 
    gap: '8px', 
    marginTop: '4px' 
  },

  /* Feedback States */
  loading: { 
    textAlign: 'center', 
    padding: '60px', 
    color: '#64748b' 
  },

  error: { 
    color: '#ef4444', 
    textAlign: 'center', 
    padding: '20px' 
  },

  noData: { 
    textAlign: 'center', 
    padding: '30px', 
    color: '#94a3b8' 
  }
};
export default ReportsManagement;